import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    TextField,
    Button,
    Typography,
    Paper,
    MenuItem,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit,
    Info as InfoIcon
} from '@mui/icons-material';
import styles from './ChecklistSetting.module.scss';


export interface ChecklistAssignmentGroup {
    id: number;
    groupName: string;
    rolesAssignedLabel: string;
    rolesAssignedId: number | null;
}

export const CheckListAndToDoGroups = ({
    handleAddAssignmentClick,
    showAssignmentForm,
    assignmentGroupName,
    setAssignmentGroupName,
    assignmentRoles,
    setAssignmentRoles,
    editingAssignmentId,
    setEditingAssignmentId,
    handleDeleteAssignmentClick,
    availableRoles,
    assignmentGroups,
    handleEditAssignmentClick,
    handleCancelAssignment,
    handleSaveAssignment,
    dynamicRoles, // Add dynamicRoles prop
    loading, // Add loading prop
    canEdit = false,
    canDelete = false,
    canExecute = false,
}: {
    handleAddAssignmentClick: () => void;
    showAssignmentForm: boolean;
    assignmentGroupName: string;
    setAssignmentGroupName: (value: string) => void;
    assignmentRoles: string;
    setAssignmentRoles: (value: string) => void;
    editingAssignmentId: number;
    setEditingAssignmentId: (value: number) => void;
    handleDeleteAssignmentClick: (id: number) => void;
    availableRoles: string[];
    assignmentGroups: ChecklistAssignmentGroup[];
    handleEditAssignmentClick: (group: ChecklistAssignmentGroup) => void;
    handleCancelAssignment: () => void;
    handleSaveAssignment: () => void;
    dynamicRoles: { id: number; label: string; backgroundColor: string; textColor: string }[]; // Add type
    loading: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
}) => {
    // Character limit validation
    const isGroupNameAtLimit = assignmentGroupName.length >= 200;
    const isEditing = Boolean(editingAssignmentId);
    const canSaveAssignment = isEditing ? canEdit : canExecute;
    const isFormReadOnly = !canSaveAssignment;
    
    return (
        <div className={styles.consistentPanel}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Checklist and To-Do Assignment Groups</Typography>
                <Button
                    className={styles.button}
                    onClick={handleAddAssignmentClick}
                    disabled={!canExecute}
                    title={canExecute ? 'Add assignment group' : 'You do not have permission to add'}
                    style={{
                        cursor: canExecute ? 'pointer' : 'not-allowed',
                        opacity: canExecute ? 1 : 0.6,
                    }}
                >
                    Add
                </Button>
            </Box>
            {/* Assignment Form */}
            <Collapse in={showAssignmentForm}>
                <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <TextField
                        label="Group Name"
                        value={assignmentGroupName}
                        onChange={(e) => setAssignmentGroupName(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 200 }}
                        error={isGroupNameAtLimit}
                        helperText={isGroupNameAtLimit ? 'Maximum 200 characters allowed' : ''}
                        disabled={isFormReadOnly}
                    />
                    <TextField
                        select
                        label="Roles Assigned"
                        value={assignmentRoles}
                        onChange={(e) => setAssignmentRoles(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={isFormReadOnly}
                    >
                        <MenuItem value="">-- Select Role --</MenuItem>
                        {Array.isArray(dynamicRoles) && dynamicRoles.length > 0 ? (
                            dynamicRoles.map((role) => (
                                <MenuItem key={role.id} value={role.id} >
                                    {role.label}
                                </MenuItem>
                            ))
                        ) : (
                            availableRoles.map((role, index) => (
                                <MenuItem key={index} value={role}>
                                    {role}
                                </MenuItem>
                            ))
                        )}
                    </TextField>
                    <Button
                        className={styles.button}
                        onClick={handleSaveAssignment}
                        disabled={isFormReadOnly || !assignmentGroupName.trim() || !assignmentRoles}
                        title={canSaveAssignment ? 'Save' : 'You do not have permission to save'}
                    >
                        Save
                    </Button>
                    <Button
                        className={styles.button}
                        onClick={handleCancelAssignment}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                </Box>
            </Collapse>
            <div className={styles.tableContainer}>
                <table className={styles.workplaceTable}>
                    <thead>
                        <tr>
                            <th>Group Name</th>
                            <th>Roles Assigned</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                                    <CircularProgress size={20} color="inherit" />
                                </td>
                            </tr>
                        ) : assignmentGroups.length === 0 ? (
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                                    No records found.
                                </td>
                            </tr>
                        ) : (
                            assignmentGroups.map((group) => (
                                <tr key={group.id}>
                                    <td>{group.groupName}</td>
                                    <td>{group.rolesAssignedLabel}</td>
                                    <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                        <button
                                            className={styles.iconButton}
                                            onClick={() => handleEditAssignmentClick(group)}
                                            title={canEdit ? 'Edit' : 'You do not have permission to edit'}
                                            disabled={!canEdit}
                                            style={{
                                                opacity: canEdit ? 1 : 0.5,
                                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                            }}
                                        >
                                            <Edit className={styles.editIcon} />
                                        </button>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                        {assignmentGroups.length > 1 ? (
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => handleDeleteAssignmentClick(group.id)}
                                                title={canDelete ? 'Delete' : 'You do not have permission to delete'}
                                                disabled={!canDelete}
                                                style={{
                                                    opacity: canDelete ? 1 : 0.5,
                                                    cursor: canDelete ? 'pointer' : 'not-allowed',
                                                }}
                                            >
                                                <DeleteIcon className={styles.deleteIcon} />
                                            </button>
                                        ) : (
                                            <Tooltip title="Cannot delete the last assignment group">
                                                <button
                                                    className={styles.iconButton}
                                                    disabled
                                                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                >
                                                    <DeleteIcon className={styles.deleteIcon} />
                                                </button>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
